import { EntityCard } from '../shared/EntityCard';
import { Clock, Trophy } from 'lucide-react';
import { Goal } from '@/types/goals';
import { formatStatus, formatDate } from '@/utils/formatters';
import { cn } from '@/utils/cn';

interface GoalCardProps {
  goal: Goal;
  onEdit?: (goal: Goal) => void;
  onDelete?: (goal: Goal) => void;
  onClick?: (goal: Goal) => void;
  className?: string;
  isDragging?: boolean;
  dragHandleProps?: any;
  showPriority?: boolean;
}

export function GoalCard({
  goal,
  onEdit,
  onDelete,
  onClick,
  className,
  isDragging = false,
  dragHandleProps,
  showPriority = false
}: GoalCardProps) {
  const completedLists = goal.lists.filter(l => l.completed).length;
  const totalLists = goal.lists.length;
  const isCompleted = goal.status === 'completed';

  return (
    <EntityCard
      testId="goal-card"
      title={goal.title}
      description={goal.description}
      status={goal.status}
      statusLabel={formatStatus(goal.status)}
      priority={goal.priority}
      showPriority={showPriority}
      progress={goal.progress}
      deadline={goal.deadline}
      isCompleted={isCompleted}
      completedDate={goal.completedDate}
      className={cn('goal-card', className)}
      isDragging={isDragging}
      dragHandleProps={dragHandleProps}
      onEdit={onEdit ? () => onEdit(goal) : undefined}
      onDelete={onDelete ? () => onDelete(goal) : undefined}
      onClick={onClick ? () => onClick(goal) : undefined}
      metadata={
        <>
          {/* SMART Criteria Preview */}
          <div className="bg-muted/30 p-3 rounded-lg space-y-2 text-xs">
            <div className="font-medium text-foreground">SMART Goal</div>
            <div className="space-y-1">
              <div><span className="font-medium">Specific:</span> {goal.smartCriteria.specific.substring(0, 60)}...</div>
              <div><span className="font-medium">Measurable:</span> {goal.smartCriteria.measurable.substring(0, 60)}...</div>
            </div>
          </div>

          {totalLists > 0 && (
            <div className="entity-card-meta">
              <span>{completedLists}/{totalLists} lists completed</span>
            </div>
          )}

          {goal.successMetrics && goal.successMetrics.length > 0 && (
            <div className="entity-card-meta">
              <Clock className="h-4 w-4" />
              <span>
                {goal.successMetrics.length} success metric{goal.successMetrics.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </>
      }
      footer={
        isCompleted ? (
          <div className="entity-card-celebration">
            <div className="entity-card-celebration-title">
              <Trophy className="h-4 w-4" />
              Goal Achieved!
            </div>
            {goal.completedDate && (
              <div className="entity-card-celebration-date">
                Completed on {formatDate(goal.completedDate)}
              </div>
            )}
          </div>
        ) : undefined
      }
    />
  );
}
